(() => {
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    if (number == null || isNaN(number)) return '0';

    const abs = Math.abs(Number(number));
    const parts = (abs / 100).toFixed(precision).split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return `${number < 0 ? '-' : ''}${dollars}${centsPart}`;
  }

  function centsValue(cents) {
    if (typeof cents === 'string') {
      const cleaned = cents.replace(/[^\d.-]/g, '');
      if (cleaned.includes('.')) return Math.round(parseFloat(cleaned) * 100);
      return parseInt(cleaned, 10);
    }
    return Number(cents);
  }

  function formatMoneyFromCents(cents, format) {
    const moneyFormat = format || '{{amount}}';
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = moneyFormat.match(placeholderRegex);
    const value = centsValue(cents);
    let formatted;

    switch (match?.[1]) {
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = formatWithDelimiters(value, 0, ' ');
        break;
      case 'amount_with_space_separator':
        formatted = formatWithDelimiters(value, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        formatted = formatWithDelimiters(value, 2, ' ', '.');
        break;
      default:
        formatted = formatWithDelimiters(value, 2);
    }

    if (!match) return formatted;
    return moneyFormat.replace(placeholderRegex, formatted);
  }

  function normalizeSize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+/g, '');
  }

  function titleForSize(originalTitle, optionValue) {
    if (!originalTitle) return originalTitle;
    const sizeInOption = String(optionValue || '').match(/\b(20|30|50|100)\s*ml\b/i);
    if (!sizeInOption) return originalTitle;
    const nextSize = sizeInOption[0].replace(/\s+/g, '').toLowerCase();
    if (/\b(20|30|50|100)\s*ml\b/i.test(originalTitle)) {
      return originalTitle.replace(/\b(20|30|50|100)\s*ml\b/i, nextSize);
    }
    return originalTitle;
  }

  function sizeToken(optionValue) {
    const match = String(optionValue || '').match(/\b(20|30|50|100)\s*ml\b/i);
    return match ? match[0].replace(/\s+/g, '').toLowerCase() : '';
  }

  function urlForSelectedSize(baseHref, optionValue) {
    const nextSize = sizeToken(optionValue);
    try {
      const url = new URL(baseHref, window.location.origin);
      if (nextSize && /-(20|30|50|100)ml(?=-|\/|$)/i.test(url.pathname)) {
        url.pathname = url.pathname.replace(/-(20|30|50|100)ml(?=-|\/|$)/i, `-${nextSize}`);
      }
      url.searchParams.delete('variant');
      return url.pathname + url.search + url.hash;
    } catch (e) {
      const base = String(baseHref || '').split('?')[0].split('#')[0];
      if (nextSize && /-(20|30|50|100)ml(?=-|$)/i.test(base)) {
        return base.replace(/-(20|30|50|100)ml(?=-|$)/i, `-${nextSize}`);
      }
      return base;
    }
  }

  function initCard(card) {
    if (!card || card.dataset.fiInit === 'true') return;
    card.dataset.fiInit = 'true';

    const variantsJson = card.querySelector('[data-fi-variants]');
    const variantPricesJson = card.querySelector('[data-fi-variant-prices]');
    const priceEl = card.querySelector('[data-fi-price]');
    const variantIdInput = card.querySelector('[data-fi-variant-id]');
    const titleEl = card.querySelector('[data-fi-title]');
    const originalTitle = card.getAttribute('data-fi-original-title') || titleEl?.textContent?.trim() || '';
    const moneyFormat = card.getAttribute('data-fi-money-format') || '';

    let variants = [];
    let variantPrices = {};
    try {
      variants = JSON.parse(variantsJson?.textContent || '[]');
    } catch (e) {}
    try {
      variantPrices = JSON.parse(variantPricesJson?.textContent || '{}');
    } catch (e) {}

    const findVariant = (optionIndex, optionValue) => {
      const wanted = normalizeSize(optionValue);
      const matches = (v) => {
        const option = v.options?.[optionIndex] || v.option1 || v.title || '';
        const check = normalizeSize(option);
        return check === wanted || check.includes(wanted);
      };
      return variants.find((v) => matches(v) && v.available) || variants.find(matches);
    };

    const setVariantByOption = (optionIndex, optionValue, sizeBtn) => {
      const next = findVariant(optionIndex, optionValue);
      const siblingUrl = sizeBtn?.getAttribute('data-fi-product-url') || '';
      const originalUrl = card.getAttribute('data-fi-original-url') || '';
      if (!next && !siblingUrl) return;

      if (next) {
        if (variantIdInput) variantIdInput.value = String(next.id);
        if (priceEl) {
          priceEl.textContent =
            variantPrices[next.id] || formatMoneyFromCents(next.price, moneyFormat);
        }
      }

      const nextTitle = titleForSize(originalTitle, optionValue);
      if (titleEl && nextTitle) titleEl.textContent = nextTitle;
      if (nextTitle) {
        card.setAttribute('aria-label', nextTitle);
        card.querySelectorAll('[data-fi-product-link]').forEach((link) => {
          link.setAttribute('aria-label', nextTitle);
        });
        const addBtn = card.querySelector('.fi-card__plus');
        if (addBtn) addBtn.setAttribute('aria-label', `Add ${nextTitle} to cart`);
      }

      // Point card clicks at the selected size product (sibling handles like *-50ml),
      // not just ?variant= on the original 100ml product.
      card.querySelectorAll('[data-fi-product-link]').forEach((link) => {
        if (siblingUrl) {
          link.href = siblingUrl;
          return;
        }
        link.href = urlForSelectedSize(originalUrl || link.href, optionValue);
      });

      card.querySelectorAll('[data-fi-size]').forEach((btn) => {
        const isActive = normalizeSize(btn.getAttribute('data-fi-option-value')) === normalizeSize(optionValue);
        btn.classList.toggle('fi-card__size--active', isActive);
      });
    };

    card.addEventListener('click', (e) => {
      const sizeBtn = e.target.closest?.('[data-fi-size]');
      if (sizeBtn) {
        e.preventDefault();
        e.stopPropagation();
        const optionIndex = Number(sizeBtn.getAttribute('data-fi-option-index') || 0);
        const optionValue = sizeBtn.getAttribute('data-fi-option-value') || '';
        setVariantByOption(optionIndex, optionValue, sizeBtn);
        return;
      }
    });
  }

  function resetSwiperEl(swiperEl) {
    if (swiperEl._featuredInSwiper) {
      swiperEl._featuredInSwiper.destroy(true, true);
      swiperEl._featuredInSwiper = null;
    }

    swiperEl.classList.remove('swiper-initialized', 'swiper-horizontal', 'swiper-backface-hidden');
    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    if (wrapper) {
      wrapper.style.transform = '';
      wrapper.style.transitionDuration = '';
    }

    swiperEl.querySelectorAll('.swiper-slide').forEach((slide) => {
      slide.style.width = '';
      slide.style.marginRight = '';
      slide.style.height = '';
    });
  }

  const LOGOS_MARQUEE_MQ = window.matchMedia('(max-width: 1199px)');

  function initLogosMarquee(marquee) {
    if (!marquee || marquee.dataset.fiLogosMarqueeInit === 'true') return;
    marquee.dataset.fiLogosMarqueeInit = 'true';

    const strip = marquee.querySelector('[data-fi-logos-strip]');
    const originalTrack = marquee.querySelector('[data-fi-logos-track]');
    if (!strip || !originalTrack) return;

    const speed = parseFloat(marquee.dataset.fiLogosSpeed) || 40;
    let position = 0;
    let contentWidth = 0;
    let paused = false;
    let lastTimestamp = null;
    let rafId = null;
    let active = false;

    function removeClones() {
      strip.querySelectorAll('[data-fi-logos-clone]').forEach((el) => el.remove());
    }

    function buildClones() {
      removeClones();
      contentWidth = originalTrack.getBoundingClientRect().width;
      if (contentWidth <= 0) return;

      const viewportWidth = marquee.getBoundingClientRect().width;
      const clonesNeeded = Math.max(1, Math.ceil((viewportWidth * 2) / contentWidth));

      for (let i = 0; i < clonesNeeded; i++) {
        const clone = originalTrack.cloneNode(true);
        clone.setAttribute('data-fi-logos-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('role');
        clone.removeAttribute('aria-label');
        clone.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
        strip.appendChild(clone);
      }
    }

    function tick(timestamp) {
      if (lastTimestamp === null) lastTimestamp = timestamp;
      const delta = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;

      if (active && !paused && contentWidth > 0) {
        position -= speed * delta;
        if (Math.abs(position) >= contentWidth) {
          position += contentWidth;
        }
        strip.style.transform = `translateX(${position}px)`;
      }

      rafId = requestAnimationFrame(tick);
    }

    function stopMarquee() {
      active = false;
      position = 0;
      strip.style.transform = '';
      removeClones();
    }

    function startMarquee() {
      buildClones();
      active = true;
      if (rafId === null) {
        rafId = requestAnimationFrame(tick);
      }
    }

    function updateMode() {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (LOGOS_MARQUEE_MQ.matches && !reduceMotion) {
        marquee.style.overflowX = 'hidden';
        startMarquee();
      } else {
        marquee.style.overflowX = reduceMotion && LOGOS_MARQUEE_MQ.matches ? 'auto' : '';
        stopMarquee();
      }
    }

    marquee.addEventListener('mouseenter', () => {
      paused = true;
    });
    marquee.addEventListener('mouseleave', () => {
      paused = false;
    });
    marquee.addEventListener('focusin', () => {
      paused = true;
    });
    marquee.addEventListener('focusout', () => {
      paused = false;
    });

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        position = 0;
        strip.style.transform = '';
        updateMode();
      }, 200);
    });

    LOGOS_MARQUEE_MQ.addEventListener('change', updateMode);
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', updateMode);

    updateMode();
  }

  function initFeaturedIn(root) {
    const prev = root.querySelector('[data-featured-in-prev]');
    const next = root.querySelector('[data-featured-in-next]');

    const swiperEl = root.querySelector('.featured-in__swiper.swiper');
    if (swiperEl && typeof Swiper !== 'undefined') {
      resetSwiperEl(swiperEl);

      const updateNavDisabled = (swiper) => {
        if (prev) {
          const disabled = swiper.isBeginning;
          prev.classList.toggle('swiper-button-disabled', disabled);
          prev.toggleAttribute('disabled', disabled);
          prev.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        }
        if (next) {
          const disabled = swiper.isEnd;
          next.classList.toggle('swiper-button-disabled', disabled);
          next.toggleAttribute('disabled', disabled);
          next.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        }
      };

      const swiper = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 16,
        loop: false,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        breakpoints: {
          767: { slidesPerView: 1.6, spaceBetween: 12 },
          1024: { slidesPerView: 2.5, spaceBetween: 24 },
        },
        navigation:
          prev && next
            ? {
                prevEl: prev,
                nextEl: next,
                disabledClass: 'swiper-button-disabled',
              }
            : undefined,
        on: {
          init: updateNavDisabled,
          slideChange: updateNavDisabled,
          progress: updateNavDisabled,
          transitionEnd: updateNavDisabled,
          touchEnd: updateNavDisabled,
          reachBeginning: updateNavDisabled,
          reachEnd: updateNavDisabled,
          fromEdge: updateNavDisabled,
          resize: updateNavDisabled,
          update: updateNavDisabled,
          observerUpdate: updateNavDisabled,
        },
      });

      swiper.update();
      updateNavDisabled(swiper);
      swiperEl._featuredInSwiper = swiper;
    }

    root.querySelectorAll('[data-fi-card]').forEach(initCard);
  }

  function initWishlistHeroButtons(root = document) {
    root.querySelectorAll('.fi-card .wishlist-hero-custom-button').forEach((wishlistButton) => {
      // Keep retrying until Wishlist Hero injects its button/icon.
      if (wishlistButton.querySelector('button, .wlh-button, [class*="wishlist-hero"] svg:not(.fi-card__wishlist-icon)')) {
        wishlistButton.dataset.wlhInit = 'true';
        return;
      }

      document.dispatchEvent(
        new CustomEvent('wishlist-hero-add-to-custom-element', {
          detail: wishlistButton,
        })
      );
    });
  }

  function initAllFiCards(root = document) {
    root.querySelectorAll('[data-fi-card]').forEach(initCard);
    initWishlistHeroButtons(root);
  }

  function boot() {
    document.querySelectorAll('[data-featured-in-slider]').forEach(initFeaturedIn);
    document.querySelectorAll('[data-fi-logos-marquee]').forEach(initLogosMarquee);
    initAllFiCards();

    // Wishlist Hero often loads after theme JS — retry a few times.
    [500, 1500, 3000].forEach((delay) => {
      window.setTimeout(() => initWishlistHeroButtons(), delay);
    });
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(boot, 150);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('product-grid:updated', () => initAllFiCards());
})();

